/**
 * User data processing utilities
 *
 * Extracted from useUserManagement.js for testability.
 * Contains pure functions for transforming user data.
 */

import { PENDING_THRESHOLD_MS } from './constants';

/**
 * Determine user status based on confirmation and last modified date
 * @param {string} cognitoStatus - Cognito user status (e.g., 'CONFIRMED')
 * @param {string|Date} lastModifiedDate - When user was last modified
 * @returns {string} 'Active' | 'Pending' | 'Inactive'
 */
export function getUserStatus(cognitoStatus, lastModifiedDate) {
  if (cognitoStatus === 'CONFIRMED') {
    return 'Active';
  }

  const timeSinceModified = Date.now() - new Date(lastModifiedDate).getTime();
  return timeSinceModified < PENDING_THRESHOLD_MS ? 'Pending' : 'Inactive';
}

/**
 * Extract email from Cognito user attributes
 * @param {Array} attributes - Cognito user attributes array
 * @returns {string|null} Lowercase email or null if not found
 */
export function extractEmailFromAttributes(attributes) {
  if (!Array.isArray(attributes)) return null;

  for (const attribute of attributes) {
    if (attribute.Name === 'email') {
      return attribute.Value.toLowerCase();
    }
  }
  return null;
}

/**
 * Process raw user data from Cognito API response
 * @param {Array} userData - Array of Cognito user objects
 * @param {Array} roleData - Array of role assignments
 * @param {string} organization - Organization name
 * @returns {Object} Cleaned user data object keyed by email
 */
export function processUserData(userData, roleData, organization) {
  const cleanedUserData = {};

  // Process Cognito users
  for (const user of userData) {
    if (!user.hasOwnProperty('Attributes')) continue;

    const username = extractEmailFromAttributes(user.Attributes);
    if (!username) continue;

    cleanedUserData[username] = {
      ...user,
      UserStatus: getUserStatus(user.UserStatus, user.UserLastModifiedDate),
      organization,
    };
  }

  // Merge role data
  for (const role of roleData) {
    if (!role.hasOwnProperty('user') || !role.hasOwnProperty('Roles')) continue;

    const username = role.user.toLowerCase();
    if (!cleanedUserData.hasOwnProperty(username)) continue;

    cleanedUserData[username].roles = role.Roles;
    // Use the user's persisted default org from their membership record, NOT the org that was
    // queried. The user list is built by querying every accessible org and merging last-wins, so
    // keying off the queried org would re-tag a multi-org user's default to whichever org was
    // iterated last (e.g. a newly-created org added to their organization_access).
    cleanedUserData[username].organization = role.organization || organization;
    cleanedUserData[username].organization_access = role.organization_access || [organization];
    cleanedUserData[username].escalationTier = role.escalationTier || 1;
  }

  return cleanedUserData;
}

/**
 * Filter users based on search criteria
 * @param {Object} users - Users object keyed by email
 * @param {Object} filters - Filter criteria
 * @param {string} [filters.searchText] - Text to search in usernames
 * @param {Array} [filters.organizations] - Organizations to include
 * @param {Array} [filters.roles] - Roles to include
 * @param {Array} [filters.statuses] - Statuses to include
 * @returns {Object} Filtered users object
 */
export function filterUsers(users, filters = {}) {
  const { searchText = '', organizations = [], roles = [], statuses = [], escalationTiers = [] } = filters;

  const searchRegex = searchText ? new RegExp(`\\b${searchText}`, 'i') : null;

  return Object.keys(users).reduce((acc, key) => {
    const user = users[key];

    // Text search filter
    if (searchRegex && !searchRegex.test(key)) {
      return acc;
    }

    // Organization filter — match on membership (the user's default org OR any org in their
    // access list), so filtering by an org shows everyone who belongs to it, not only users
    // whose persisted default happens to be that org.
    if (organizations.length) {
      const memberOrgs = user.organization_access || (user.organization ? [user.organization] : []);
      if (!memberOrgs.some((o) => organizations.includes(o))) {
        return acc;
      }
    }

    // Role filter
    if (roles.length && !user.roles?.some((role) => roles.includes(role))) {
      return acc;
    }

    // Status filter
    if (statuses.length && !statuses.includes(user.UserStatus)) {
      return acc;
    }

    // Escalation tier filter
    if (escalationTiers.length && !escalationTiers.includes(user.escalationTier || 1)) {
      return acc;
    }

    acc[key] = user;
    return acc;
  }, {});
}

/**
 * Merge user data from multiple organizations
 * @param {Array<Object>} userDataSets - Array of user data objects
 * @returns {Object} Merged user data
 */
export function mergeUserData(userDataSets) {
  return userDataSets.reduce((acc, userData) => ({ ...acc, ...userData }), {});
}
